import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  DECLINED,
  DISPATCH_REQUESTED,
  IGNORED,
} from '@rsos/constants/alertStatuses';
import {
  backfillAlerts,
  receivedAlertUpdatedStatus,
  removeAlert,
  setAlertIgnoredBy,
  setAlertRead,
  setSelectedAlertID,
} from '../alerts/incidentsSlice';
import {
  removeAllCalls,
  removeCall,
  setSelectedCallerID,
} from '../emergencyCalls/emergencyCallsSlice';
import { playAlertNotificationIfNecessary } from './alertNotifications';
import {
  AudioNotificationsState,
  AudioNotificationsGlobalState,
  AudioNotificationStatus,
  NOTIFICATION_COMPLETE,
  AudioNotificationStatusCategories,
} from './audioNotificationTypes';
import { playCallNotificationIfNecessary } from './locationNotifications';
import {
  playUnreadMessageNotificationIfNecessary,
  playUnreadAlertMessageNotificationIfNecessary,
} from './textMessageNotifications';

const initialState: AudioNotificationsState = {
  calls: {},
  textMessages: {},
  alertMessages: {},
  alerts: {},
  suppressedAudio: '',
};

/**
 * Notifications can play or not play, can repeat or not repeat, and can expire
 * or not expire. Calling this action is roughly idempotent; once an alert is
 * playing, it'll play until repeatsRemaining is 0 or the expiration timestamp
 * has passed. (It's not *perfectly* idempotent, in that re-calling this for a
 * repeating notification can cause it to re-play a few seconds early, but very
 * few notifications are set to repeat more than once.)
 */
export const playNotification = createAsyncThunk<
  {
    category: AudioNotificationStatusCategories;
    id: string;
    status?: AudioNotificationStatus;
    expiration?: number;
    isInitial?: boolean;
  },
  {
    category: AudioNotificationStatusCategories;
    id: string;
    expiration?: number;
    isInitial?: boolean;
  },
  {
    state: AudioNotificationsGlobalState;
  }
>(
  'chats/playNotification',
  async ({ category, id, expiration, isInitial }, { getState, dispatch }) => {
    try {
      const state = getState();
      // If this alert has been sent, seen, or expired, we're all done.
      const previousStatus = selectNotificationStatus(state, category, id);
      if (
        (previousStatus && previousStatus.repeatsRemaining <= 0) ||
        (previousStatus?.expiration && previousStatus.expiration < Date.now())
      ) {
        return { category, id, status: previousStatus };
      }
      // Function to dispatch repeats for alerts that repeat more than once:
      const repeater = () => {
        dispatch(playNotification({ category, id, expiration }));
      };
      // The default status is a completed alert.
      let status: AudioNotificationStatus | undefined = NOTIFICATION_COMPLETE();

      // Why not have one async action for each category? Because it complicates repeats.
      switch (category) {
        case 'calls':
          status = await playCallNotificationIfNecessary(state, id, repeater);
          break;
        case 'alerts':
          status = await playAlertNotificationIfNecessary(state, id, repeater);
          break;
        case 'textMessages':
          status = await playUnreadMessageNotificationIfNecessary(
            state,
            id,
            isInitial,
          );
          break;
        case 'alertMessages':
          status = await playUnreadAlertMessageNotificationIfNecessary(
            state,
            id,
          );
          break;
      }
      return { category, id, status };
    } catch (e) {
      // Thunks swallow errors, so:
      // eslint-disable-next-line no-console
      console.error(e);
      return { category, id };
    }
  },
);

const audioNotificationsSlice = createSlice({
  name: 'audioNotifications',
  initialState,
  reducers: {
    setSuppressedAudio: (state, action: PayloadAction<string>) => {
      state.suppressedAudio = action.payload || '';
    },
    clearNotification: (
      state,
      action: PayloadAction<{
        category: AudioNotificationStatusCategories;
        id: string;
      }>,
    ) => {
      state[action.payload.category][action.payload.id] =
        NOTIFICATION_COMPLETE();
    },
  },
  extraReducers: builder => {
    builder
      .addCase(playNotification.fulfilled, (state, action) => {
        if (!action.payload.status) return;
        state[action.payload.category][action.payload.id] =
          action.payload.status;
      })

      .addCase(removeAllCalls, state => {
        state.calls = {};
      })
      // Individually removing a call still doesn't fully restore its
      // notifiability -- see 'ignoredCalls' -- but including this feels tidy and
      // symmetrical:
      .addCase(removeCall, (state, action) => {
        delete state.calls[action.payload];
      })
      .addCase(setSelectedCallerID, (state, action) => {
        state.calls[action.payload] = NOTIFICATION_COMPLETE();
      })
      .addCase(setSelectedAlertID, (state, action) => {
        state.alerts[action.payload] = NOTIFICATION_COMPLETE();
      })
      .addCase(setAlertRead, (state, action) => {
        state.alerts[action.payload] = NOTIFICATION_COMPLETE();
      })
      .addCase(setAlertIgnoredBy, (state, action) => {
        state.alerts[action.payload.alertID] = NOTIFICATION_COMPLETE();
      })
      .addCase(receivedAlertUpdatedStatus, (state, action) => {
        state.alerts[action.payload.alert_id] = NOTIFICATION_COMPLETE();
      })
      .addCase(removeAlert, (state, action) => {
        delete state.alerts[action.payload];
      })
      // A backfill applies status changes and removals outside the live
      // actions above, so a ringing alert that was handled or removed during
      // the outage has to stop repeating here too.
      .addCase(backfillAlerts.fulfilled, (state, action) => {
        action.payload.forEach(alert => {
          const alertID = alert?.alert_id;
          if (!alertID || !(alertID in state.alerts)) {
            return;
          }
          const statusName = alert.status?.name;
          if (statusName === IGNORED || statusName === DECLINED) {
            delete state.alerts[alertID];
          } else if (statusName !== DISPATCH_REQUESTED) {
            state.alerts[alertID] = NOTIFICATION_COMPLETE();
          }
        });
      });
  },
});

export function selectNotificationStatus(
  state: { audioNotifications: AudioNotificationsState },
  category: AudioNotificationStatusCategories,
  id: string,
) {
  return state.audioNotifications[category][id];
}

export const { clearNotification, setSuppressedAudio } =
  audioNotificationsSlice.actions;
export default audioNotificationsSlice.reducer;
